import { Suspense, lazy } from "react";
import { Navigate, Outlet } from "react-router-dom";
import AuthGuard from "../guards/AuthGuard";
import RoleGuard from "@/guards/RoleGuard";
import DashboardLayout from "../layouts/dashboard";
import {
  PATH_SETTINGS,
  PATH_SETTINGS_AVAILABILITY,
  PATH_SETTINGS_BILLING,
  PATH_SETTINGS_CALL_CENTER,
  PATH_SETTINGS_DOMAIN_CONNECT,
  PATH_SETTINGS_DOMAINS,
  PATH_SETTINGS_EMAIL_TEMPLATES,
  PATH_SETTINGS_SNIPPETS,
  PATH_SETTINGS_FIELDS,
  PATH_SETTINGS_INTEGRATIONS,
  PATH_SETTINGS_INTEGRATIONS_GOOGLE_ADS,
  PATH_SETTINGS_INTEGRATIONS_SLACK,
  PATH_SETTINGS_INTEGRATIONS_HUBSPOT,
  PATH_SETTINGS_INTEGRATIONS_META,
  PATH_SETTINGS_PIXEL_INSTALL,
  PATH_SETTINGS_PROFILE,
  PATH_SETTINGS_TRAINING,
  PATH_SETTINGS_USER_MANAGEMENT,
  PATH_SETTINGS_EMAIL,
} from "./paths";
import { getRelativePath } from "@/utils/paths";
import Loader from "@/components/Loader";

const Settings = lazy(() => import("@/pages/settings/settings"));
const ProfileSettings = lazy(() => import("@/pages/settings/profile"));
const AvailabilitySettings = lazy(() => import("@/pages/settings/availability"));
const ProfileUserManagement = lazy(() => import("@/pages/settings/user-management"));
const ProfileBilling = lazy(() => import("@/pages/settings/billing"));
const IntegrationsContainer = lazy(() => import("@/pages/settings/integrations/integrations.container"));
const HubspotIntegration = lazy(() => import("@/pages/settings/integrations/hubspot/hubspot.settings.container"));
const MetaIntegration = lazy(() => import("@/pages/settings/integrations/meta/meta.settings.container"));
const GoogleAdsSettings = lazy(() => import("@/pages/settings/integrations/google-ads/google.ads.settings.container"));
const SlackSettings = lazy(() => import("@/pages/settings/integrations/slack/slack.settings.container"));
const PixelInstallContainer = lazy(() => import("@/pages/settings/pixel-install"));
const DomainsContainer = lazy(() => import("@/pages/settings/domains"));
const DomainConnect = lazy(() => import("@/pages/settings/domains/connect"));
const FieldsContainer = lazy(() => import("@/pages/settings/fields"));
const CallCenterSettings = lazy(() => import("@/pages/settings/call-center"));
const TrainingSettings = lazy(() => import("@/pages/settings/training"));
const EmailTemplatesSettings = lazy(() => import("@/pages/settings/library/email-templates-settings-tab").then((m) => ({ default: m.EmailTemplatesSettingsTab })));
const SnippetsSettings = lazy(() => import("@/pages/settings/library/snippets-settings-tab").then((m) => ({ default: m.SnippetsSettingsTab })));

const SettingsRoot = (
  <AuthGuard>
    <RoleGuard>
      <DashboardLayout>
        <Suspense fallback={<Loader />}>
          <Outlet />
        </Suspense>
      </DashboardLayout>
    </RoleGuard>
  </AuthGuard>
);

const settingsRoutes = [
  {
    path: PATH_SETTINGS,
    element: SettingsRoot,
    children: [
      {
        index: true,
        element: <Navigate to={PATH_SETTINGS_PROFILE} replace />,
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_PROFILE),
        element: (
          <Settings>
            <ProfileSettings />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_AVAILABILITY),
        element: (
          <Settings>
            <AvailabilitySettings />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_USER_MANAGEMENT),
        element: (
          <Settings>
            <ProfileUserManagement />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_INTEGRATIONS),
        element: (
          <Settings>
            <IntegrationsContainer />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_BILLING),
        element: (
          <Settings>
            <ProfileBilling />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_PIXEL_INSTALL),
        element: (
          <Settings>
            <PixelInstallContainer />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_DOMAINS),
        element: (
          <Settings>
            <DomainsContainer />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_DOMAIN_CONNECT),
        element: <DomainConnect />,
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_FIELDS),
        element: (
          <Settings>
            <FieldsContainer />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_CALL_CENTER),
        element: (
          <Settings>
            <CallCenterSettings />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_TRAINING),
        element: (
          <Settings>
            <TrainingSettings />
          </Settings>
        ),
      },
      {
        // Email settings now live in a tab of the user-level profile page.
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_EMAIL),
        element: <Navigate to={`${PATH_SETTINGS_PROFILE}?tab=email`} replace />,
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_EMAIL_TEMPLATES),
        element: (
          <Settings>
            <EmailTemplatesSettings />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_SNIPPETS),
        element: (
          <Settings>
            <SnippetsSettings />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_INTEGRATIONS_HUBSPOT),
        element: <HubspotIntegration />,
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_INTEGRATIONS_META),
        element: <MetaIntegration />,
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_INTEGRATIONS_GOOGLE_ADS),
        element: <GoogleAdsSettings />,
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_INTEGRATIONS_SLACK),
        element: <SlackSettings />,
      },
    ],
  },
];

export default settingsRoutes;